import { api } from './baseAPI';
export const addTagTypes = ['API Discovery', 'Dashboard'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getApiResources: build.query<GetApiResourcesApiResponse, GetApiResourcesApiArg>({
        query: () => ({ url: `/` }),
        providesTags: ['API Discovery'],
      }),
      listDashboard: build.query<ListDashboardApiResponse, ListDashboardApiArg>({
        query: (queryArg) => ({
          url: `/dashboards`,
          params: {
            pretty: queryArg.pretty,
            allowWatchBookmarks: queryArg.allowWatchBookmarks,
            continue: queryArg['continue'],
            fieldSelector: queryArg.fieldSelector,
            labelSelector: queryArg.labelSelector,
            limit: queryArg.limit,
            resourceVersion: queryArg.resourceVersion,
            resourceVersionMatch: queryArg.resourceVersionMatch,
            sendInitialEvents: queryArg.sendInitialEvents,
            shardSelector: queryArg.shardSelector,
            timeoutSeconds: queryArg.timeoutSeconds,
            watch: queryArg.watch,
          },
        }),
        providesTags: ['Dashboard'],
      }),
      createDashboard: build.mutation<CreateDashboardApiResponse, CreateDashboardApiArg>({
        query: (queryArg) => ({
          url: `/dashboards`,
          method: 'POST',
          body: queryArg.dashboard,
          params: {
            pretty: queryArg.pretty,
            dryRun: queryArg.dryRun,
            fieldManager: queryArg.fieldManager,
            fieldValidation: queryArg.fieldValidation,
          },
        }),
        invalidatesTags: ['Dashboard'],
      }),
      getDashboard: build.query<GetDashboardApiResponse, GetDashboardApiArg>({
        query: (queryArg) => ({
          url: `/dashboards/${queryArg.name}`,
          params: {
            pretty: queryArg.pretty,
          },
        }),
        providesTags: ['Dashboard'],
      }),
      replaceDashboard: build.mutation<ReplaceDashboardApiResponse, ReplaceDashboardApiArg>({
        query: (queryArg) => ({
          url: `/dashboards/${queryArg.name}`,
          method: 'PUT',
          body: queryArg.dashboard,
          params: {
            pretty: queryArg.pretty,
            dryRun: queryArg.dryRun,
            fieldManager: queryArg.fieldManager,
            fieldValidation: queryArg.fieldValidation,
          },
        }),
        invalidatesTags: ['Dashboard'],
      }),
      deleteDashboard: build.mutation<DeleteDashboardApiResponse, DeleteDashboardApiArg>({
        query: (queryArg) => ({
          url: `/dashboards/${queryArg.name}`,
          method: 'DELETE',
          params: {
            pretty: queryArg.pretty,
            dryRun: queryArg.dryRun,
            gracePeriodSeconds: queryArg.gracePeriodSeconds,
            ignoreStoreReadErrorWithClusterBreakingPotential: queryArg.ignoreStoreReadErrorWithClusterBreakingPotential,
            orphanDependents: queryArg.orphanDependents,
            propagationPolicy: queryArg.propagationPolicy,
          },
        }),
        invalidatesTags: ['Dashboard'],
      }),
      updateDashboard: build.mutation<UpdateDashboardApiResponse, UpdateDashboardApiArg>({
        query: (queryArg) => ({
          url: `/dashboards/${queryArg.name}`,
          method: 'PATCH',
          body: queryArg.patch,
          params: {
            pretty: queryArg.pretty,
            dryRun: queryArg.dryRun,
            fieldManager: queryArg.fieldManager,
            fieldValidation: queryArg.fieldValidation,
            force: queryArg.force,
          },
        }),
        invalidatesTags: ['Dashboard'],
      }),
      getDashboardDto: build.query<GetDashboardDtoApiResponse, GetDashboardDtoApiArg>({
        query: (queryArg) => ({ url: `/dashboards/${queryArg.name}/dto` }),
        providesTags: ['Dashboard'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as generatedAPI };
export type GetApiResourcesApiResponse = /** status 200 OK */ ApiResourceList;
export type GetApiResourcesApiArg = void;
export type ListDashboardApiResponse = /** status 200 OK */ DashboardList;
export type ListDashboardApiArg = {
  /** If 'true', then the output is pretty printed. Defaults to 'false' unless the user-agent indicates a browser or command-line HTTP tool (curl and wget). */
  pretty?: string;
  /** allowWatchBookmarks requests watch events with type "BOOKMARK". Servers that do not implement bookmarks may ignore this flag and bookmarks are sent at the server's discretion. Clients should not assume bookmarks are returned at any specific interval, nor may they assume the server will send any BOOKMARK event during a session. If this is not a watch, this field is ignored. */
  allowWatchBookmarks?: boolean;
  /** The continue option should be set when retrieving more results from the server. Since this value is server defined, clients may only use the continue value from a previous query result with identical query parameters (except for the value of continue) and the server may reject a continue value it does not recognize. If the specified continue value is no longer valid whether due to expiration (generally five to fifteen minutes) or a configuration change on the server, the server will respond with a 410 ResourceExpired error together with a continue token. If the client needs a consistent list, it must restart their list without the continue field. Otherwise, the client may send another list request with the token received with the 410 error, the server will respond with a list starting from the next key, but from the latest snapshot, which is inconsistent from the previous list results - objects that are created, modified, or deleted after the first list request will be included in the response, as long as their keys are after the "next key".
    
    This field is not supported when watch is true. Clients may start a watch from the last resourceVersion value returned by the server and not miss any modifications. */
  continue?: string;
  /** A selector to restrict the list of returned objects by their fields. Defaults to everything. */
  fieldSelector?: string;
  /** A selector to restrict the list of returned objects by their labels. Defaults to everything. */
  labelSelector?: string;
  /** limit is a maximum number of responses to return for a list call. If more items exist, the server will set the `continue` field on the list metadata to a value that can be used with the same initial query to retrieve the next set of results. Setting a limit may return fewer than the requested amount of items (up to zero items) in the event all requested objects are filtered out and clients should only use the presence of the continue field to determine whether more results are available. Servers may choose not to support the limit argument and will return all of the available results. If limit is specified and the continue field is empty, clients may assume that no more results are available. This field is not supported if watch is true.
    
    The server guarantees that the objects returned when using continue will be identical to issuing a single list call without a limit - that is, no objects created, modified, or deleted after the first request is issued will be included in any subsequent continued requests. This is sometimes referred to as a consistent snapshot, and ensures that a client that is using limit to receive smaller chunks of a very large result can ensure they see all possible objects. If objects are updated during a chunked list the version of the object that was present at the time the first list result was calculated is returned. */
  limit?: number;
  /** resourceVersion sets a constraint on what resource versions a request may be served from. See https://kubernetes.io/docs/reference/using-api/api-concepts/#resource-versions for details.
    
    Defaults to unset */
  resourceVersion?: string;
  /** resourceVersionMatch determines how resourceVersion is applied to list calls. It is highly recommended that resourceVersionMatch be set for list calls where resourceVersion is set See https://kubernetes.io/docs/reference/using-api/api-concepts/#resource-versions for details.
    
    Defaults to unset */
  resourceVersionMatch?: string;
  /** `sendInitialEvents=true` may be set together with `watch=true`. In that case, the watch stream will begin with synthetic events to produce the current state of objects in the collection. Once all such events have been sent, a synthetic "Bookmark" event  will be sent. The bookmark will report the ResourceVersion (RV) corresponding to the set of objects, and be marked with `"k8s.io/initial-events-end": "true"` annotation. Afterwards, the watch stream will proceed as usual, sending watch events corresponding to changes (subsequent to the RV) to objects watched.
    
    When `sendInitialEvents` option is set, we require `resourceVersionMatch` option to also be set. The semantic of the watch request is as following: - `resourceVersionMatch` = NotOlderThan
      is interpreted as "data at least as new as the provided `resourceVersion`"
      and the bookmark event is send when the state is synced
      to a `resourceVersion` at least as fresh as the one provided by the ListOptions.
      If `resourceVersion` is unset, this is interpreted as "consistent read" and the
      bookmark event is send when the state is synced at least to the moment
      when request started being processed.
    - `resourceVersionMatch` set to any other value or unset
      Invalid error is returned.
    
    Defaults to true if `resourceVersion=""` or `resourceVersion="0"` (for backward compatibility reasons) and to false otherwise. */
  sendInitialEvents?: boolean;
  /** shardSelector restricts the list of returned objects using a CEL-based shard selector expression. The format uses the shardRange() function combined with || (logical OR) to specify one or more hash ranges:
    
      shardRange(object.metadata.uid, '0x0', '0x8000000000000000')
      shardRange(object.metadata.uid, '0x0', '0x8000000000000000') || shardRange(object.metadata.uid, '0x8000000000000000', '0x10000000000000000')
    
    Field paths use CEL-style object-rooted syntax (e.g. "object.metadata.uid"), NOT the fieldSelector format ("metadata.uid"). Currently supported paths:
      - object.metadata.uid
      - object.metadata.namespace
    
    hexStart and hexEnd are single-quoted CEL string literals with a '0x' prefix, defining the inclusive lower and exclusive upper bounds over the 64-bit FNV-1a hash space. The full range is [0x0, 0x10000000000000000), where the exclusive upper bound equals 2^64.
    
    Examples:
      2-shard split:
        shard 0: shardRange(object.metadata.uid, '0x0000000000000000', '0x8000000000000000')
        shard 1: shardRange(object.metadata.uid, '0x8000000000000000', '0x10000000000000000')
      4-shard split:
        shard 0: shardRange(object.metadata.uid, '0x0000000000000000', '0x4000000000000000')
        shard 1: shardRange(object.metadata.uid, '0x4000000000000000', '0x8000000000000000')
        shard 2: shardRange(object.metadata.uid, '0x8000000000000000', '0xc000000000000000')
        shard 3: shardRange(object.metadata.uid, '0xc000000000000000', '0x10000000000000000')
    
    This is an alpha field and requires enabling the ShardedListAndWatch feature gate. */
  shardSelector?: string;
  /** Timeout for the list/watch call. This limits the duration of the call, regardless of any activity or inactivity. */
  timeoutSeconds?: number;
  /** Watch for changes to the described resources and return them as a stream of add, update, and remove notifications. Specify resourceVersion. */
  watch?: boolean;
};
export type CreateDashboardApiResponse = /** status 200 OK */
  | Dashboard
  | /** status 201 Created */ Dashboard
  | /** status 202 Accepted */ Dashboard;
export type CreateDashboardApiArg = {
  /** If 'true', then the output is pretty printed. Defaults to 'false' unless the user-agent indicates a browser or command-line HTTP tool (curl and wget). */
  pretty?: string;
  /** When present, indicates that modifications should not be persisted. An invalid or unrecognized dryRun directive will result in an error response and no further processing of the request. Valid values are: - All: all dry run stages will be processed */
  dryRun?: string;
  /** fieldManager is a name associated with the actor or entity that is making these changes. The value must be less than or 128 characters long, and only contain printable characters, as defined by https://golang.org/pkg/unicode/#IsPrint. */
  fieldManager?: string;
  /** fieldValidation instructs the server on how to handle objects in the request (POST/PUT/PATCH) containing unknown or duplicate fields. Valid values are: - Ignore: This will ignore any unknown fields that are silently dropped from the object, and will ignore all but the last duplicate field that the decoder encounters. This is the default behavior prior to v1.23. - Warn: This will send a warning via the standard warning response header for each unknown field that is dropped from the object, and for each duplicate field that is encountered. The request will still succeed if there are no other errors, and will only persist the last of any duplicate fields. This is the default in v1.23+ - Strict: This will fail the request with a BadRequest error if any unknown fields would be dropped from the object, or if any duplicate fields are present. The error returned from the server will contain all unknown and duplicate fields encountered. */
  fieldValidation?: string;
  dashboard: Dashboard;
};
export type GetDashboardApiResponse = /** status 200 OK */ Dashboard;
export type GetDashboardApiArg = {
  /** name of the Dashboard */
  name: string;
  /** If 'true', then the output is pretty printed. Defaults to 'false' unless the user-agent indicates a browser or command-line HTTP tool (curl and wget). */
  pretty?: string;
};
export type ReplaceDashboardApiResponse = /** status 200 OK */ Dashboard | /** status 201 Created */ Dashboard;
export type ReplaceDashboardApiArg = {
  /** name of the Dashboard */
  name: string;
  /** If 'true', then the output is pretty printed. Defaults to 'false' unless the user-agent indicates a browser or command-line HTTP tool (curl and wget). */
  pretty?: string;
  /** When present, indicates that modifications should not be persisted. An invalid or unrecognized dryRun directive will result in an error response and no further processing of the request. Valid values are: - All: all dry run stages will be processed */
  dryRun?: string;
  /** fieldManager is a name associated with the actor or entity that is making these changes. The value must be less than or 128 characters long, and only contain printable characters, as defined by https://golang.org/pkg/unicode/#IsPrint. */
  fieldManager?: string;
  /** fieldValidation instructs the server on how to handle objects in the request (POST/PUT/PATCH) containing unknown or duplicate fields. Valid values are: - Ignore: This will ignore any unknown fields that are silently dropped from the object, and will ignore all but the last duplicate field that the decoder encounters. This is the default behavior prior to v1.23. - Warn: This will send a warning via the standard warning response header for each unknown field that is dropped from the object, and for each duplicate field that is encountered. The request will still succeed if there are no other errors, and will only persist the last of any duplicate fields. This is the default in v1.23+ - Strict: This will fail the request with a BadRequest error if any unknown fields would be dropped from the object, or if any duplicate fields are present. The error returned from the server will contain all unknown and duplicate fields encountered. */
  fieldValidation?: string;
  dashboard: Dashboard;
};
export type DeleteDashboardApiResponse = /** status 200 OK */ Status | /** status 202 Accepted */ Status;
export type DeleteDashboardApiArg = {
  /** name of the Dashboard */
  name: string;
  /** If 'true', then the output is pretty printed. Defaults to 'false' unless the user-agent indicates a browser or command-line HTTP tool (curl and wget). */
  pretty?: string;
  /** When present, indicates that modifications should not be persisted. An invalid or unrecognized dryRun directive will result in an error response and no further processing of the request. Valid values are: - All: all dry run stages will be processed */
  dryRun?: string;
  /** The duration in seconds before the object should be deleted. Value must be non-negative integer. The value zero indicates delete immediately. If this value is nil, the default grace period for the specified type will be used. Defaults to a per object value if not specified. zero means delete immediately. */
  gracePeriodSeconds?: number;
  /** if set to true, it will trigger an unsafe deletion of the resource in case the normal deletion flow fails with a corrupt object error. A resource is considered corrupt if it can not be retrieved from the underlying storage successfully because of a) its data can not be transformed e.g. decryption failure, or b) it fails to decode into an object. NOTE: unsafe deletion ignores finalizer constraints, skips precondition checks, and removes the object from the storage. WARNING: This may potentially break the cluster if the workload associated with the resource being unsafe-deleted relies on normal deletion flow. Use only if you REALLY know what you are doing. The default value is false, and the user must opt in to enable it */
  ignoreStoreReadErrorWithClusterBreakingPotential?: boolean;
  /** Deprecated: please use the PropagationPolicy, this field will be deprecated in 1.7. Should the dependent objects be orphaned. If true/false, the "orphan" finalizer will be added to/removed from the object's finalizers list. Either this field or PropagationPolicy may be set, but not both. */
  orphanDependents?: boolean;
  /** Whether and how garbage collection will be performed. Either this field or OrphanDependents may be set, but not both. The default policy is decided by the existing finalizer set in the metadata.finalizers and the resource-specific default policy. Acceptable values are: 'Orphan' - orphan the dependents; 'Background' - allow the garbage collector to delete the dependents in the background; 'Foreground' - a cascading policy that deletes all dependents in the foreground. */
  propagationPolicy?: string;
};
export type UpdateDashboardApiResponse = /** status 200 OK */ Dashboard | /** status 201 Created */ Dashboard;
export type UpdateDashboardApiArg = {
  /** name of the Dashboard */
  name: string;
  /** If 'true', then the output is pretty printed. Defaults to 'false' unless the user-agent indicates a browser or command-line HTTP tool (curl and wget). */
  pretty?: string;
  /** When present, indicates that modifications should not be persisted. An invalid or unrecognized dryRun directive will result in an error response and no further processing of the request. Valid values are: - All: all dry run stages will be processed */
  dryRun?: string;
  /** fieldManager is a name associated with the actor or entity that is making these changes. The value must be less than or 128 characters long, and only contain printable characters, as defined by https://golang.org/pkg/unicode/#IsPrint. This field is required for apply requests (application/apply-patch) but optional for non-apply patch types (JsonPatch, MergePatch, StrategicMergePatch). */
  fieldManager?: string;
  /** fieldValidation instructs the server on how to handle objects in the request (POST/PUT/PATCH) containing unknown or duplicate fields. Valid values are: - Ignore: This will ignore any unknown fields that are silently dropped from the object, and will ignore all but the last duplicate field that the decoder encounters. This is the default behavior prior to v1.23. - Warn: This will send a warning via the standard warning response header for each unknown field that is dropped from the object, and for each duplicate field that is encountered. The request will still succeed if there are no other errors, and will only persist the last of any duplicate fields. This is the default in v1.23+ - Strict: This will fail the request with a BadRequest error if any unknown fields would be dropped from the object, or if any duplicate fields are present. The error returned from the server will contain all unknown and duplicate fields encountered. */
  fieldValidation?: string;
  /** Force is going to "force" Apply requests. It means user will re-acquire conflicting fields owned by other people. Force flag must be unset for non-apply patch requests. */
  force?: boolean;
  patch: Patch;
};
export type GetDashboardDtoApiResponse = /** status 200 OK */ DashboardWithAccessInfo;
export type GetDashboardDtoApiArg = {
  /** name of the DashboardWithAccessInfo */
  name: string;
};
export type ApiResource = {
  /** categories is a list of the grouped resources this resource belongs to (e.g. 'all') */
  categories?: string[];
  /** group is the preferred group of the resource.  Empty implies the group of the containing resource list. For subresources, this may have a different value, for example: Scale". */
  group?: string;
  /** kind is the kind for the resource (e.g. 'Foo' is the kind for a resource 'foo') */
  kind: string;
  /** name is the plural name of the resource. */
  name: string;
  /** namespaced indicates if a resource is namespaced or not. */
  namespaced: boolean;
  /** shortNames is a list of suggested short names of the resource. */
  shortNames?: string[];
  /** singularName is the singular name of the resource.  This allows clients to handle plural and singular opaquely. The singularName is more correct for reporting status on a single item and both singular and plural are allowed from the kubectl CLI interface. */
  singularName: string;
  /** The hash value of the storage version, the version this resource is converted to when written to the data store. Value must be treated as opaque by clients. Only equality comparison on the value is valid. This is an alpha feature and may change or be removed in the future. The field is populated by the apiserver only if the StorageVersionHash feature gate is enabled. This field will remain optional even if it graduates. */
  storageVersionHash?: string;
  /** verbs is a list of supported kube verbs (this includes get, list, watch, create, update, patch, delete, deletecollection, and proxy) */
  verbs: string[];
  /** version is the preferred version of the resource.  Empty implies the version of the containing resource list For subresources, this may have a different value, for example: v1 (while inside a v1beta1 version of the core resource's group)". */
  version?: string;
};
export type ApiResourceList = {
  /** APIVersion defines the versioned schema of this representation of an object. Servers should convert recognized schemas to the latest internal value, and may reject unrecognized values. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources */
  apiVersion?: string;
  /** groupVersion is the group and version this APIResourceList is for. */
  groupVersion: string;
  /** Kind is a string value representing the REST resource this object represents. Servers may infer this from the endpoint the client submits requests to. Cannot be updated. In CamelCase. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds */
  kind?: string;
  /** resources contains the name of the resources and if they are namespaced. */
  resources: ApiResource[];
};
export type Time = string;
export type FieldsV1 = object;
export type ManagedFieldsEntry = {
  /** APIVersion defines the version of this resource that this field set applies to. The format is "group/version" just like the top-level APIVersion field. It is necessary to track the version of a field set because it cannot be automatically converted. */
  apiVersion?: string;
  /** FieldsType is the discriminator for the different fields format and version. There is currently only one possible value: "FieldsV1" */
  fieldsType?: string;
  /** FieldsV1 holds the first JSON version format as described in the "FieldsV1" type. */
  fieldsV1?: FieldsV1;
  /** Manager is an identifier of the workflow managing these fields. */
  manager?: string;
  /** Operation is the type of operation which lead to this ManagedFieldsEntry being created. The only valid values for this field are 'Apply' and 'Update'. */
  operation?: string;
  /** Subresource is the name of the subresource used to update that object, or empty string if the object was updated through the main resource. The value of this field is used to distinguish between managers, even if they share the same name. For example, a status update will be distinct from a regular update using the same manager name. Note that the APIVersion field is not related to the Subresource field and it always corresponds to the version of the main resource. */
  subresource?: string;
  /** Time is the timestamp of when the ManagedFields entry was added. The timestamp will also be updated if a field is added, the manager changes any of the owned fields value or removes a field. The timestamp does not update when a field is removed from the entry because another manager took it over. */
  time?: Time;
};
export type OwnerReference = {
  /** API version of the referent. */
  apiVersion: string;
  /** If true, AND if the owner has the "foregroundDeletion" finalizer, then the owner cannot be deleted from the key-value store until this reference is removed. See https://kubernetes.io/docs/concepts/architecture/garbage-collection/#foreground-deletion for how the garbage collector interacts with this field and enforces the foreground deletion. Defaults to false. To set this field, a user needs "delete" permission of the owner, otherwise 422 (Unprocessable Entity) will be returned. */
  blockOwnerDeletion?: boolean;
  /** If true, this reference points to the managing controller. */
  controller?: boolean;
  /** Kind of the referent. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds */
  kind: string;
  /** Name of the referent. More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names#names */
  name: string;
  /** UID of the referent. More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names#uids */
  uid: string;
};
export type ObjectMeta = {
  /** Annotations is an unstructured key value map stored with a resource that may be set by external tools to store and retrieve arbitrary metadata. They are not queryable and should be preserved when modifying objects. More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/annotations */
  annotations?: {
    [key: string]: string;
  };
  /** CreationTimestamp is a timestamp representing the server time when this object was created. It is not guaranteed to be set in happens-before order across separate operations. Clients may not set this value. It is represented in RFC3339 form and is in UTC.
    
    Populated by the system. Read-only. Null for lists. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata */
  creationTimestamp?: Time;
  /** Number of seconds allowed for this object to gracefully terminate before it will be removed from the system. Only set when deletionTimestamp is also set. May only be shortened. Read-only. */
  deletionGracePeriodSeconds?: number;
  /** DeletionTimestamp is RFC 3339 date and time at which this resource will be deleted. This field is set by the server when a graceful deletion is requested by the user, and is not directly settable by a client. The resource is expected to be deleted (no longer visible from resource lists, and not reachable by name) after the time in this field, once the finalizers list is empty. As long as the finalizers list contains items, deletion is blocked. Once the deletionTimestamp is set, this value may not be unset or be set further into the future, although it may be shortened or the resource may be deleted prior to this time. For example, a user may request that a pod is deleted in 30 seconds. The Kubelet will react by sending a graceful termination signal to the containers in the pod. After that 30 seconds, the Kubelet will send a hard termination signal (SIGKILL) to the container and after cleanup, remove the pod from the API. In the presence of network partitions, this object may still exist after this timestamp, until an administrator or automated process can determine the resource is fully terminated. If not set, graceful deletion of the object has not been requested.
    
    Populated by the system when a graceful deletion is requested. Read-only. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata */
  deletionTimestamp?: Time;
  /** Must be empty before the object is deleted from the registry. Each entry is an identifier for the responsible component that will remove the entry from the list. If the deletionTimestamp of the object is non-nil, entries in this list can only be removed. Finalizers may be processed and removed in any order.  Order is NOT enforced because it introduces significant risk of stuck finalizers. finalizers is a shared field, any actor with permission can reorder it. If the finalizer list is processed in order, then this can lead to a situation in which the component responsible for the first finalizer in the list is waiting for a signal (field value, external system, or other) produced by a component responsible for a finalizer later in the list, resulting in a deadlock. Without enforced ordering finalizers are free to order amongst themselves and are not vulnerable to ordering changes in the list. */
  finalizers?: string[];
  /** GenerateName is an optional prefix, used by the server, to generate a unique name ONLY IF the Name field has not been provided. If this field is used, the name returned to the client will be different than the name passed. This value will also be combined with a unique suffix. The provided value has the same validation rules as the Name field, and may be truncated by the length of the suffix required to make the value unique on the server.
    
    If this field is specified and the generated name exists, the server will return a 409.
    
    Applied only if Name is not specified. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#idempotency */
  generateName?: string;
  /** A sequence number representing a specific generation of the desired state. Populated by the system. Read-only. */
  generation?: number;
  /** Map of string keys and values that can be used to organize and categorize (scope and select) objects. May match selectors of replication controllers and services. More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/labels */
  labels?: {
    [key: string]: string;
  };
  /** ManagedFields maps workflow-id and version to the set of fields that are managed by that workflow. This is mostly for internal housekeeping, and users typically shouldn't need to set or understand this field. A workflow can be the user's name, a controller's name, or the name of a specific apply path like "ci-cd". The set of fields is always in the version that the workflow used when modifying the object. */
  managedFields?: ManagedFieldsEntry[];
  /** Name must be unique within a namespace. Is required when creating resources, although some resources may allow a client to request the generation of an appropriate name automatically. Name is primarily intended for creation idempotence and configuration definition. Cannot be updated. More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names#names */
  name?: string;
  /** Namespace defines the space within which each name must be unique. An empty namespace is equivalent to the "default" namespace, but "default" is the canonical representation. Not all objects are required to be scoped to a namespace - the value of this field for those objects will be empty.
    
    Must be a DNS_LABEL. Cannot be updated. More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/namespaces */
  namespace?: string;
  /** List of objects depended by this object. If ALL objects in the list have been deleted, this object will be garbage collected. If this object is managed by a controller, then an entry in this list will point to this controller, with the controller field set to true. There cannot be more than one managing controller. */
  ownerReferences?: OwnerReference[];
  /** An opaque value that represents the internal version of this object that can be used by clients to determine when objects have changed. May be used for optimistic concurrency, change detection, and the watch operation on a resource or set of resources. Clients must treat these values as opaque and passed unmodified back to the server. They may only be valid for a particular resource or set of resources.
    
    Populated by the system. Read-only. Value must be treated as opaque by clients and . More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#concurrency-control-and-consistency */
  resourceVersion?: string;
  /** Deprecated: selfLink is a legacy read-only field that is no longer populated by the system. */
  selfLink?: string;
  /** UID is the unique in time and space value for this object. It is typically generated by the server on successful creation of a resource and is not allowed to change on PUT operations.
    
    Populated by the system. Read-only. More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names#uids */
  uid?: string;
};
export type DashboardAnnotationPanelFilter = {
  /** Should the specified panels be included or excluded */
  exclude?: boolean;
  /** Panel IDs that should be included or excluded */
  ids: number[];
};
export type DashboardAnnotationEventFieldMapping = {
  /** Regular expression to apply to the field value */
  regex?: string;
  /** Source type for the field value */
  source?: string;
  /** Constant value to use when source is "text" */
  value?: string;
};
export type DashboardV2DataQueryKindDatasource = {
  name?: string;
};
export type DashboardDataQueryKind = {
  /** New type for datasource reference Not creating a new type until we figure out how to handle DS refs for group by, adhoc, and every place that uses DataSourceRef in TS. */
  datasource?: DashboardV2DataQueryKindDatasource;
  group: string;
  kind: string;
  labels?: {
    [key: string]: string;
  };
  spec: {
    [key: string]: object;
  };
  version: string;
};
export type DashboardAnnotationQuerySpec = {
  builtIn?: boolean;
  enable: boolean;
  filter?: DashboardAnnotationPanelFilter;
  hide: boolean;
  iconColor: string;
  /** Catch-all field for datasource-specific properties. Should not be available in as code tooling. */
  legacyOptions?: {
    [key: string]: object;
  };
  /** Mappings define how to convert data frame fields to annotation event fields. */
  mappings?: {
    [key: string]: DashboardAnnotationEventFieldMapping;
  };
  name: string;
  /** Placement can be used to display the annotation query somewhere else on the dashboard other than the default location. */
  placement?: string;
  query: DashboardDataQueryKind;
};
export type DashboardAnnotationQueryKind = {
  kind: string;
  spec: DashboardAnnotationQuerySpec;
};
export type DashboardLibraryPanelRef = {
  /** Library panel name */
  name: string;
  /** Library panel uid */
  uid: string;
};
export type DashboardLibraryPanelKindSpec = {
  /** Panel ID for the library panel in the dashboard */
  id: number;
  libraryPanel: DashboardLibraryPanelRef;
  /** Title for the library panel in the dashboard */
  title: string;
};
export type DashboardLibraryPanelKind = {
  kind: string;
  spec: DashboardLibraryPanelKindSpec;
};
export type DashboardPanelQuerySpec = {
  hidden: boolean;
  query: DashboardDataQueryKind;
  refId: string;
};
export type DashboardPanelQueryKind = {
  kind: string;
  spec: DashboardPanelQuerySpec;
};
export type DashboardQueryOptionsSpec = {
  cacheTimeout?: string;
  hideTimeOverride?: boolean;
  interval?: string;
  maxDataPoints?: number;
  queryCachingTTL?: number;
  timeCompare?: string;
  timeFrom?: string;
  timeShift?: string;
};
export type DashboardMatcherConfig = {
  /** The matcher id. This is used to find the matcher implementation from registry. */
  id: string;
  /** The matcher options. This is specific to the matcher implementation. */
  options?: object;
  /** If set, limits this matcher to fields of that type. If not set, "series" mode is used. */
  scope?: string;
};
export type DashboardTransformationSpec = {
  /** Disabled transformations are skipped */
  disabled?: boolean;
  /** Optional frame matcher. When missing it will be applied to all results */
  filter?: DashboardMatcherConfig;
  /** Options to be passed to the transformer Valid options depend on the transformer id */
  options: object;
  /** Where to pull DataFrames from as input to transformation */
  topic?: string;
};
export type DashboardTransformationKind = {
  /** The group is the transformation ID */
  group: string;
  kind: string;
  spec: DashboardTransformationSpec;
};
export type DashboardQueryGroupSpec = {
  queries: DashboardPanelQueryKind[];
  queryOptions: DashboardQueryOptionsSpec;
  transformations: DashboardTransformationKind[];
};
export type DashboardQueryGroupKind = {
  kind: string;
  spec: DashboardQueryGroupSpec;
};
export type DashboardDataLink = {
  targetBlank?: boolean;
  title: string;
  url: string;
};
export type DashboardFetchOptions = {
  body?: string;
  headers?: string[][];
  method: string;
  /** These are 2D arrays of strings, each representing a key-value pair We are defining them this way because we can't generate a go struct that that would have exactly two strings in each sub-array */
  queryParams?: string[][];
  url: string;
};
export type DashboardInfinityOptions = {
  body?: string;
  datasourceUid: string;
  headers?: string[][];
  method: string;
  /** These are 2D arrays of strings, each representing a key-value pair We are defining them this way because we can't generate a go struct that that would have exactly two strings in each sub-array */
  queryParams?: string[][];
  url: string;
};
export type DashboardV2ActionStyle = {
  backgroundColor?: string;
};
export type DashboardActionVariable = {
  key: string;
  name: string;
  type: string;
};
export type DashboardAction = {
  confirmation?: string;
  fetch?: DashboardFetchOptions;
  infinity?: DashboardInfinityOptions;
  oneClick?: boolean;
  style?: DashboardV2ActionStyle;
  title: string;
  type: string;
  variables?: DashboardActionVariable[];
};
export type DashboardFieldColor = {
  /** The fixed color value for fixed or shades color modes. */
  fixedColor?: string;
  /** The main color scheme mode. */
  mode: string;
  /** Some visualizations need to know how to assign a series color from by value color schemes. */
  seriesBy?: string;
};
export type DashboardValueMappingResult = {
  /** Text to use when the value matches */
  color?: string;
  /** Icon to display when the value matches. Only specific visualizations. */
  icon?: string;
  /** Position in the mapping array. Only used internally. */
  index?: number;
  /** Text to display when the value matches */
  text?: string;
};
export type DashboardV2RangeMapOptions = {
  /** Min value of the range. It can be null which means -Infinity */
  from: number;
  /** Config to apply when the value is within the range */
  result: DashboardValueMappingResult;
  /** Max value of the range. It can be null which means +Infinity */
  to: number;
};
export type DashboardRangeMap = {
  /** Range to match against and the result to apply when the value is within the range */
  options: DashboardV2RangeMapOptions;
  type: string;
};
export type DashboardV2RegexMapOptions = {
  /** Regular expression to match against */
  pattern: string;
  /** Config to apply when the value matches the regex */
  result: DashboardValueMappingResult;
};
export type DashboardRegexMap = {
  /** Regular expression to match against and the result to apply when the value matches the regex */
  options: DashboardV2RegexMapOptions;
  type: string;
};
export type DashboardV2SpecialValueMapOptions = {
  /** Special value to match against */
  match: string;
  /** Config to apply when the value matches the special value */
  result: DashboardValueMappingResult;
};
export type DashboardSpecialValueMap = {
  options: DashboardV2SpecialValueMapOptions;
  type: string;
};
export type DashboardValueMap = {
  /** Map with <value_to_match>: ValueMappingResult. For example: { "10": { text: "Perfection!", color: "green" } } */
  options: {
    [key: string]: DashboardValueMappingResult;
  };
  type: string;
};
export type DashboardValueMapOrRangeMapOrRegexMapOrSpecialValueMap = {
  RangeMap?: DashboardRangeMap;
  RegexMap?: DashboardRegexMap;
  SpecialValueMap?: DashboardSpecialValueMap;
  ValueMap?: DashboardValueMap;
};
export type DashboardThreshold = {
  color: string;
  /** Value null means -Infinity */
  value: number;
};
export type DashboardThresholdsConfig = {
  mode: string;
  steps: DashboardThreshold[];
};
export type DashboardFieldConfig = {
  /** Define interactive HTTP requests that can be triggered from data visualizations. */
  actions?: DashboardAction[];
  /** Panel color configuration */
  color?: DashboardFieldColor;
  /** custom is specified by the FieldConfig field in panel plugin schemas. */
  custom?: {
    [key: string]: object;
  };
  /** Specify the number of decimals Grafana includes in the rendered value. If you leave this field blank, Grafana automatically truncates the number of decimals based on the value. For example 1.1234 will display as 1.12 and 100.456 will display as 100. To display all decimals, set the unit to `String`. */
  decimals?: number;
  /** Human readable field metadata */
  description?: string;
  /** The display value for this field.  This supports template variables blank is auto */
  displayName?: string;
  /** This can be used by data sources that return and explicit naming structure for values and labels When this property is configured, this value is used rather than the default naming strategy. */
  displayNameFromDS?: string;
  /** Calculate min max per field */
  fieldMinMax?: boolean;
  /** True if data source field supports ad-hoc filters */
  filterable?: boolean;
  /** The behavior when clicking on a result */
  links?: object[];
  /** Convert input values into a display string */
  mappings?: DashboardValueMapOrRangeMapOrRegexMapOrSpecialValueMap[];
  /** The maximum value used in percentage threshold calculations. Leave blank for auto calculation based on all series and fields. */
  max?: number;
  /** The minimum value used in percentage threshold calculations. Leave blank for auto calculation based on all series and fields. */
  min?: number;
  /** Alternative to empty string */
  noValue?: string;
  /** How null values should be handled when calculating field stats "null" - Include null values, "connected" - Ignore nulls, "null as zero" - Treat nulls as zero */
  nullValueMode?: string;
  /** An explicit path to the field in the datasource.  When the frame meta includes a path, This will default to `${frame.meta.path}/${field.name}
    
    When defined, this value can be used as an identifier within the datasource scope, and may be used to update the results */
  path?: string;
  /** Map numeric values to states */
  thresholds?: DashboardThresholdsConfig;
  /** Unit a field should use. The unit you select is applied to all fields except time. You can use the units ID available in Grafana or a custom unit. Available units in Grafana: https://github.com/grafana/grafana/blob/main/packages/grafana-data/src/valueFormats/categories.ts As custom unit, you can use the following formats: `suffix:<suffix>` for custom unit that should go after value. `prefix:<prefix>` for custom unit that should go before value. `time:<format>` For custom date time formats type for example `time:YYYY-MM-DD`. `si:<base scale><unit characters>` for custom SI units. For example: `si: mF`. This one is a bit more advanced as you can specify both a unit and the source data scale. So if your source data is represented as milli (thousands of) something prefix the unit with that SI scale character. `count:<unit>` for a custom count unit. `currency:<unit>` for custom a currency unit. */
  unit?: string;
  /** True if data source can write a value to the path. Auth/authz are supported separately */
  writeable?: boolean;
};
export type DashboardDynamicConfigValue = {
  id: string;
  value?: object;
};
export type DashboardV2FieldConfigSourceOverrides = {
  /** Describes config override rules created when interacting with Grafana. */
  __systemRef?: string;
  matcher: DashboardMatcherConfig;
  properties: DashboardDynamicConfigValue[];
};
export type DashboardFieldConfigSource = {
  /** Defaults are the options applied to all fields. */
  defaults: DashboardFieldConfig;
  /** Overrides are the options applied to specific fields overriding the defaults. */
  overrides: DashboardV2FieldConfigSourceOverrides[];
};
export type DashboardVizConfigSpec = {
  fieldConfig: DashboardFieldConfigSource;
  options: {
    [key: string]: object;
  };
};
export type DashboardVizConfigKind = {
  /** The group is the plugin ID */
  group: string;
  kind: string;
  spec: DashboardVizConfigSpec;
  version: string;
};
export type DashboardPanelSpec = {
  data: DashboardQueryGroupKind;
  description: string;
  id: number;
  links: DashboardDataLink[];
  title: string;
  transparent?: boolean;
  vizConfig: DashboardVizConfigKind;
};
export type DashboardPanelKind = {
  kind: string;
  spec: DashboardPanelSpec;
};
export type DashboardPanelKindOrLibraryPanelKind = {
  LibraryPanelKind?: DashboardLibraryPanelKind;
  PanelKind?: DashboardPanelKind;
};
export type DashboardConditionalRenderingDataSpec = {
  value: boolean;
};
export type DashboardConditionalRenderingDataKind = {
  kind: string;
  spec: DashboardConditionalRenderingDataSpec;
};
export type DashboardConditionalRenderingTimeRangeSizeSpec = {
  value: string;
};
export type DashboardConditionalRenderingTimeRangeSizeKind = {
  kind: string;
  spec: DashboardConditionalRenderingTimeRangeSizeSpec;
};
export type DashboardConditionalRenderingVariableSpec = {
  operator: string;
  value: string;
  variable: string;
};
export type DashboardConditionalRenderingVariableKind = {
  kind: string;
  spec: DashboardConditionalRenderingVariableSpec;
};
export type DashboardConditionalRenderingVariableKindOrConditionalRenderingDataKindOrConditionalRenderingTimeRangeSizeKind =
  {
    ConditionalRenderingDataKind?: DashboardConditionalRenderingDataKind;
    ConditionalRenderingTimeRangeSizeKind?: DashboardConditionalRenderingTimeRangeSizeKind;
    ConditionalRenderingVariableKind?: DashboardConditionalRenderingVariableKind;
  };
export type DashboardConditionalRenderingGroupSpec = {
  condition: string;
  items: DashboardConditionalRenderingVariableKindOrConditionalRenderingDataKindOrConditionalRenderingTimeRangeSizeKind[];
  visibility: string;
};
export type DashboardConditionalRenderingGroupKind = {
  kind: string;
  spec: DashboardConditionalRenderingGroupSpec;
};
export type DashboardElementReference = {
  kind: string;
  name: string;
};
export type DashboardAutoGridRepeatOptions = {
  mode: string;
  value: string;
};
export type DashboardAutoGridLayoutItemSpec = {
  conditionalRendering?: DashboardConditionalRenderingGroupKind;
  element: DashboardElementReference;
  repeat?: DashboardAutoGridRepeatOptions;
};
export type DashboardAutoGridLayoutItemKind = {
  kind: string;
  spec: DashboardAutoGridLayoutItemSpec;
};
export type DashboardAutoGridLayoutSpec = {
  columnWidth?: number;
  columnWidthMode: string;
  fillScreen?: boolean;
  items: DashboardAutoGridLayoutItemKind[];
  maxColumnCount?: number;
  rowHeight?: number;
  rowHeightMode: string;
};
export type DashboardAutoGridLayoutKind = {
  kind: string;
  spec: DashboardAutoGridLayoutSpec;
};
export type DashboardRepeatOptions = {
  direction?: string;
  maxPerRow?: number;
  mode: string;
  value: string;
};
export type DashboardGridLayoutItemSpec = {
  /** reference to a PanelKind from dashboard.spec.elements Expressed as JSON Schema reference */
  element: DashboardElementReference;
  height: number;
  repeat?: DashboardRepeatOptions;
  width: number;
  x: number;
  y: number;
};
export type DashboardGridLayoutItemKind = {
  kind: string;
  spec: DashboardGridLayoutItemSpec;
};
export type DashboardGridLayoutSpec = {
  items: DashboardGridLayoutItemKind[];
};
export type DashboardGridLayoutKind = {
  kind: string;
  spec: DashboardGridLayoutSpec;
};
export type DashboardTabRepeatOptions = {
  mode: string;
  value: string;
};
export type DashboardV2AdhocVariableKindDatasource = {
  name?: string;
};
export type DashboardAdHocFilterWithLabels = {
  /** @deprecated */
  condition?: string;
  forceEdit?: boolean;
  key: string;
  keyLabel?: string;
  operator: string;
  origin?: string;
  value: string;
  valueLabels?: string[];
  values?: string[];
};
export type DashboardStringOrFloat64 = {
  Float64?: number;
  String?: string;
};
export type DashboardMetricFindValue = {
  expandable?: boolean;
  group?: string;
  text: string;
  value?: DashboardStringOrFloat64;
};
export type DashboardDatasourceControlSourceRef = {
  /** The plugin type-id */
  group: string;
  type: string;
};
export type DashboardAdhocVariableSpec = {
  allowCustomValue: boolean;
  baseFilters: DashboardAdHocFilterWithLabels[];
  defaultKeys: DashboardMetricFindValue[];
  description?: string;
  /** Whether the group-by operator is enabled in the ad hoc filter combobox. */
  enableGroupBy?: boolean;
  filters: DashboardAdHocFilterWithLabels[];
  hide: string;
  label?: string;
  name: string;
  origin?: DashboardDatasourceControlSourceRef;
  skipUrlSync: boolean;
};
export type DashboardAdhocVariableKind = {
  datasource?: DashboardV2AdhocVariableKindDatasource;
  group: string;
  kind: string;
  labels?: {
    [key: string]: string;
  };
  spec: DashboardAdhocVariableSpec;
};
export type DashboardStringOrArrayOfString = {
  ArrayOfString?: string[];
  String?: string;
};
export type DashboardVariableOption = {
  /** Additional properties for multi-props variables */
  properties?: {
    [key: string]: string;
  };
  /** Whether the option is selected or not */
  selected?: boolean;
  /** Text to be displayed for the option */
  text: DashboardStringOrArrayOfString;
  /** Value of the option */
  value: DashboardStringOrArrayOfString;
};
export type DashboardConstantVariableSpec = {
  current: DashboardVariableOption;
  description?: string;
  hide: string;
  label?: string;
  name: string;
  origin?: DashboardDatasourceControlSourceRef;
  query: string;
  skipUrlSync: boolean;
};
export type DashboardConstantVariableKind = {
  kind: string;
  spec: DashboardConstantVariableSpec;
};
export type DashboardCustomVariableSpec = {
  allValue?: string;
  allowCustomValue: boolean;
  current: DashboardVariableOption;
  description?: string;
  hide: string;
  includeAll: boolean;
  label?: string;
  multi: boolean;
  name: string;
  options: DashboardVariableOption[];
  origin?: DashboardDatasourceControlSourceRef;
  query: string;
  skipUrlSync: boolean;
  valuesFormat?: string;
};
export type DashboardCustomVariableKind = {
  kind: string;
  spec: DashboardCustomVariableSpec;
};
export type DashboardDatasourceVariableSpec = {
  allValue?: string;
  allowCustomValue: boolean;
  current: DashboardVariableOption;
  description?: string;
  hide: string;
  includeAll: boolean;
  label?: string;
  multi: boolean;
  name: string;
  options: DashboardVariableOption[];
  origin?: DashboardDatasourceControlSourceRef;
  pluginId: string;
  refresh: string;
  regex: string;
  skipUrlSync: boolean;
};
export type DashboardDatasourceVariableKind = {
  kind: string;
  spec: DashboardDatasourceVariableSpec;
};
export type DashboardV2GroupByVariableKindDatasource = {
  name?: string;
};
export type DashboardGroupByVariableSpec = {
  current: DashboardVariableOption;
  defaultValue?: DashboardVariableOption;
  description?: string;
  hide: string;
  label?: string;
  multi: boolean;
  name: string;
  options: DashboardVariableOption[];
  origin?: DashboardDatasourceControlSourceRef;
  skipUrlSync: boolean;
};
export type DashboardGroupByVariableKind = {
  datasource?: DashboardV2GroupByVariableKindDatasource;
  group: string;
  kind: string;
  labels?: {
    [key: string]: string;
  };
  spec: DashboardGroupByVariableSpec;
};
export type DashboardIntervalVariableSpec = {
  auto: boolean;
  auto_count: number;
  auto_min: string;
  current: DashboardVariableOption;
  description?: string;
  hide: string;
  label?: string;
  name: string;
  options: DashboardVariableOption[];
  origin?: DashboardDatasourceControlSourceRef;
  query: string;
  refresh: string;
  skipUrlSync: boolean;
};
export type DashboardIntervalVariableKind = {
  kind: string;
  spec: DashboardIntervalVariableSpec;
};
export type DashboardQueryVariableSpec = {
  allValue?: string;
  allowCustomValue: boolean;
  current: DashboardVariableOption;
  definition?: string;
  description?: string;
  hide: string;
  includeAll: boolean;
  label?: string;
  multi: boolean;
  name: string;
  options: DashboardVariableOption[];
  origin?: DashboardDatasourceControlSourceRef;
  placeholder?: string;
  query: DashboardDataQueryKind;
  refresh: string;
  regex: string;
  regexApplyTo?: string;
  skipUrlSync: boolean;
  sort: string;
  staticOptions?: DashboardVariableOption[];
  staticOptionsOrder?: string;
};
export type DashboardQueryVariableKind = {
  kind: string;
  spec: DashboardQueryVariableSpec;
};
export type DashboardSwitchVariableSpec = {
  current: string;
  description?: string;
  disabledValue: string;
  enabledValue: string;
  hide: string;
  label?: string;
  name: string;
  origin?: DashboardDatasourceControlSourceRef;
  skipUrlSync: boolean;
};
export type DashboardSwitchVariableKind = {
  kind: string;
  spec: DashboardSwitchVariableSpec;
};
export type DashboardTextVariableSpec = {
  current: DashboardVariableOption;
  description?: string;
  hide: string;
  label?: string;
  name: string;
  origin?: DashboardDatasourceControlSourceRef;
  query: string;
  skipUrlSync: boolean;
};
export type DashboardTextVariableKind = {
  kind: string;
  spec: DashboardTextVariableSpec;
};
export type DashboardQueryVariableKindOrTextVariableKindOrConstantVariableKindOrDatasourceVariableKindOrIntervalVariableKindOrCustomVariableKindOrGroupByVariableKindOrAdhocVariableKindOrSwitchVariableKind =
  {
    AdhocVariableKind?: DashboardAdhocVariableKind;
    ConstantVariableKind?: DashboardConstantVariableKind;
    CustomVariableKind?: DashboardCustomVariableKind;
    DatasourceVariableKind?: DashboardDatasourceVariableKind;
    GroupByVariableKind?: DashboardGroupByVariableKind;
    IntervalVariableKind?: DashboardIntervalVariableKind;
    QueryVariableKind?: DashboardQueryVariableKind;
    SwitchVariableKind?: DashboardSwitchVariableKind;
    TextVariableKind?: DashboardTextVariableKind;
  };
export type DashboardTabsLayoutTabSpec = {
  conditionalRendering?: DashboardConditionalRenderingGroupKind;
  layout: DashboardGridLayoutKindOrRowsLayoutKindOrAutoGridLayoutKindOrTabsLayoutKind;
  repeat?: DashboardTabRepeatOptions;
  title?: string;
  variables?: DashboardQueryVariableKindOrTextVariableKindOrConstantVariableKindOrDatasourceVariableKindOrIntervalVariableKindOrCustomVariableKindOrGroupByVariableKindOrAdhocVariableKindOrSwitchVariableKind[];
};
export type DashboardTabsLayoutTabKind = {
  kind: string;
  spec: DashboardTabsLayoutTabSpec;
};
export type DashboardTabsLayoutSpec = {
  tabs: DashboardTabsLayoutTabKind[];
};
export type DashboardTabsLayoutKind = {
  kind: string;
  spec: DashboardTabsLayoutSpec;
};
export type DashboardGridLayoutKindOrAutoGridLayoutKindOrTabsLayoutKindOrRowsLayoutKind = {
  AutoGridLayoutKind?: DashboardAutoGridLayoutKind;
  GridLayoutKind?: DashboardGridLayoutKind;
  RowsLayoutKind?: DashboardRowsLayoutKind;
  TabsLayoutKind?: DashboardTabsLayoutKind;
};
export type DashboardRowRepeatOptions = {
  mode: string;
  value: string;
};
export type DashboardRowsLayoutRowSpec = {
  collapse?: boolean;
  conditionalRendering?: DashboardConditionalRenderingGroupKind;
  fillScreen?: boolean;
  hideHeader?: boolean;
  layout: DashboardGridLayoutKindOrAutoGridLayoutKindOrTabsLayoutKindOrRowsLayoutKind;
  repeat?: DashboardRowRepeatOptions;
  title?: string;
  variables?: DashboardQueryVariableKindOrTextVariableKindOrConstantVariableKindOrDatasourceVariableKindOrIntervalVariableKindOrCustomVariableKindOrGroupByVariableKindOrAdhocVariableKindOrSwitchVariableKind[];
};
export type DashboardRowsLayoutRowKind = {
  kind: string;
  spec: DashboardRowsLayoutRowSpec;
};
export type DashboardRowsLayoutSpec = {
  rows: DashboardRowsLayoutRowKind[];
};
export type DashboardRowsLayoutKind = {
  kind: string;
  spec: DashboardRowsLayoutSpec;
};
export type DashboardGridLayoutKindOrRowsLayoutKindOrAutoGridLayoutKindOrTabsLayoutKind = {
  AutoGridLayoutKind?: DashboardAutoGridLayoutKind;
  GridLayoutKind?: DashboardGridLayoutKind;
  RowsLayoutKind?: DashboardRowsLayoutKind;
  TabsLayoutKind?: DashboardTabsLayoutKind;
};
export type DashboardDashboardLink = {
  /** If true, all dashboards links will be displayed in a dropdown. If false, all dashboards links will be displayed side by side. Only valid if the type is dashboards */
  asDropdown: boolean;
  /** Icon name to be displayed with the link */
  icon: string;
  /** If true, includes current template variables values in the link as query params */
  includeVars: boolean;
  /** If true, includes current time range in the link as query params */
  keepTime: boolean;
  /** The source that registered the link (if any) */
  origin?: DashboardDatasourceControlSourceRef;
  /** Placement can be used to display the link somewhere else on the dashboard other than above the visualisations. */
  placement?: string;
  /** List of tags to limit the linked dashboards. If empty, all dashboards will be displayed. Only valid if the type is dashboards */
  tags: string[];
  /** If true, the link will be opened in a new tab */
  targetBlank: boolean;
  /** Title to display with the link */
  title: string;
  /** Tooltip to display when the user hovers their mouse over it */
  tooltip: string;
  /** Link type. Accepted values are dashboards (to refer to another dashboard) and link (to refer to an external resource) FIXME: The type is generated as `type: DashboardLinkType | dashboardLinkType.Link;` but it should be `type: DashboardLinkType` */
  type: string;
  /** Link URL. Only required/valid if the type is link */
  url?: string;
};
export type DashboardAutoGridLayoutKindOrGridLayoutKind = {
  AutoGridLayoutKind?: DashboardAutoGridLayoutKind;
  GridLayoutKind?: DashboardGridLayoutKind;
};
export type DashboardPreferences = {
  /** default layout template to be used when new containers are created */
  layout?: DashboardAutoGridLayoutKindOrGridLayoutKind;
};
export type DashboardTimeRangeOption = {
  display: string;
  from: string;
  to: string;
};
export type DashboardTimeSettingsSpec = {
  /** Refresh rate of dashboard. Represented via interval string, e.g. "5s", "1m", "1h", "1d". v1: refresh */
  autoRefresh: string;
  /** Interval options available in the refresh picker dropdown. v1: timepicker.refresh_intervals */
  autoRefreshIntervals: string[];
  /** The month that the fiscal year starts on. 0 = January, 11 = December */
  fiscalYearStartMonth: number;
  /** Start time range for dashboard. Accepted values are relative time strings like "now-6h" or absolute time strings like "2020-07-10T08:00:00.000Z". */
  from: string;
  /** Whether timepicker is visible or not. v1: timepicker.hidden */
  hideTimepicker: boolean;
  /** Override the now time by entering a time delay. Use this option to accommodate known delays in data aggregation to avoid null values. v1: timepicker.nowDelay */
  nowDelay?: string;
  /** Selectable options available in the time picker dropdown. Has no effect on provisioned dashboard. v1: timepicker.quick_ranges , not exposed in the UI */
  quickRanges?: DashboardTimeRangeOption[];
  /** Timezone of dashboard. Accepted values are IANA TZDB zone ID or "browser" or "utc". */
  timezone?: string;
  /** End time range for dashboard. Accepted values are relative time strings like "now-6h" or absolute time strings like "2020-07-10T08:00:00.000Z". */
  to: string;
  /** Day when the week starts. Expressed by the name of the day in lowercase, e.g. "monday". */
  weekStart?: string;
};
export type DashboardSpec = {
  annotations: DashboardAnnotationQueryKind[];
  /** Configuration of dashboard cursor sync behavior. "Off" for no shared crosshair or tooltip (default). "Crosshair" for shared crosshair. "Tooltip" for shared crosshair AND shared tooltip. */
  cursorSync: string;
  /** Description of dashboard. */
  description?: string;
  /** Whether a dashboard is editable or not. */
  editable?: boolean;
  elements: {
    [key: string]: DashboardPanelKindOrLibraryPanelKind;
  };
  layout: DashboardGridLayoutKindOrRowsLayoutKindOrAutoGridLayoutKindOrTabsLayoutKind;
  /** Links with references to other dashboards or external websites. */
  links: DashboardDashboardLink[];
  /** When set to true, the dashboard will redraw panels at an interval matching the pixel width. This will keep data "moving left" regardless of the query refresh rate. This setting helps avoid dashboards presenting stale live data. */
  liveNow?: boolean;
  preferences?: DashboardPreferences;
  /** When set to true, the dashboard will load all panels in the dashboard when it's loaded. */
  preload: boolean;
  /** Plugins only. The version of the dashboard installed together with the plugin. This is used to determine if the dashboard should be updated when the plugin is updated. */
  revision?: number;
  /** Tags associated with dashboard. */
  tags: string[];
  timeSettings: DashboardTimeSettingsSpec;
  /** Title of dashboard. */
  title: string;
  /** Configured template variables. */
  variables: DashboardQueryVariableKindOrTextVariableKindOrConstantVariableKindOrDatasourceVariableKindOrIntervalVariableKindOrCustomVariableKindOrGroupByVariableKindOrAdhocVariableKindOrSwitchVariableKind[];
};
export type DashboardConversionStatus = {
  /** The error message from the conversion. Empty if the conversion has not failed. */
  error?: string;
  /** Whether from another version has failed. If true, means that the dashboard is not valid, and the caller should instead fetch the stored version. */
  failed: boolean;
  /** The original value map[string]any */
  source?: object;
  /** The version which was stored when the dashboard was created / updated. Fetching this version should always succeed. */
  storedVersion?: string;
};
export type DashboardStatus = {
  /** Optional conversion status. */
  conversion?: DashboardConversionStatus;
};
export type Dashboard = {
  /** APIVersion defines the versioned schema of this representation of an object. Servers should convert recognized schemas to the latest internal value, and may reject unrecognized values. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources */
  apiVersion?: string;
  /** Kind is a string value representing the REST resource this object represents. Servers may infer this from the endpoint the client submits requests to. Cannot be updated. In CamelCase. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds */
  kind?: string;
  metadata: ObjectMeta;
  /** Spec is the spec of the Dashboard */
  spec: DashboardSpec;
  status: DashboardStatus;
};
export type ListMeta = {
  /** continue may be set if the user set a limit on the number of items returned, and indicates that the server has more data available. The value is opaque and may be used to issue another request to the endpoint that served this list to retrieve the next set of available objects. Continuing a consistent list may not be possible if the server configuration has changed or more than a few minutes have passed. The resourceVersion field returned when using this continue value will be identical to the value in the first response, unless you have received this token from an error message. */
  continue?: string;
  /** remainingItemCount is the number of subsequent items in the list which are not included in this list response. If the list request contained label or field selectors, then the number of remaining items is unknown and the field will be left unset and omitted during serialization. If the list is complete (either because it is not chunking or because this is the last chunk), then there are no more remaining items and this field will be left unset and omitted during serialization. Servers older than v1.15 do not set this field. The intended use of the remainingItemCount is *estimating* the size of a collection. Clients should not rely on the remainingItemCount to be set or to be exact. */
  remainingItemCount?: number;
  /** String that identifies the server's internal version of this object that can be used by clients to determine when objects have changed. Value must be treated as opaque by clients and passed unmodified back to the server. Populated by the system. Read-only. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#concurrency-control-and-consistency */
  resourceVersion?: string;
  /** Deprecated: selfLink is a legacy read-only field that is no longer populated by the system. */
  selfLink?: string;
};
export type DashboardList = {
  /** APIVersion defines the versioned schema of this representation of an object. Servers should convert recognized schemas to the latest internal value, and may reject unrecognized values. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources */
  apiVersion?: string;
  items: Dashboard[];
  /** Kind is a string value representing the REST resource this object represents. Servers may infer this from the endpoint the client submits requests to. Cannot be updated. In CamelCase. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds */
  kind?: string;
  metadata: ListMeta;
};
export type StatusCause = {
  /** The field of the resource that has caused this error, as named by its JSON serialization. May include dot and postfix notation for nested attributes. Arrays are zero-indexed.  Fields may appear more than once in an array of causes due to fields having multiple errors. Optional.
    
    Examples:
      "name" - the field "name" on the current resource
      "items[0].name" - the field "name" on the first array entry in "items" */
  field?: string;
  /** A human-readable description of the cause of the error.  This field may be presented as-is to a reader. */
  message?: string;
  /** A machine-readable description of the cause of the error. If this value is empty there is no information available. */
  reason?: string;
};
export type StatusDetails = {
  /** The Causes array includes more details associated with the StatusReason failure. Not all StatusReasons may provide detailed causes. */
  causes?: StatusCause[];
  /** The group attribute of the resource associated with the status StatusReason. */
  group?: string;
  /** The kind attribute of the resource associated with the status StatusReason. On some operations may differ from the requested resource Kind. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds */
  kind?: string;
  /** The name attribute of the resource associated with the status StatusReason (when there is a single name which can be described). */
  name?: string;
  /** If specified, the time in seconds before the operation should be retried. Some errors may indicate the client must take an alternate action - for those errors this field may indicate how long to wait before taking the alternate action. */
  retryAfterSeconds?: number;
  /** UID of the resource. (when there is a single resource which can be described). More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names#uids */
  uid?: string;
};
export type Status = {
  /** APIVersion defines the versioned schema of this representation of an object. Servers should convert recognized schemas to the latest internal value, and may reject unrecognized values. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources */
  apiVersion?: string;
  /** Suggested HTTP return code for this status, 0 if not set. */
  code?: number;
  /** Extended data associated with the reason.  Each reason may define its own extended details. This field is optional and the data returned is not guaranteed to conform to any schema except that defined by the reason type. */
  details?: StatusDetails;
  /** Kind is a string value representing the REST resource this object represents. Servers may infer this from the endpoint the client submits requests to. Cannot be updated. In CamelCase. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds */
  kind?: string;
  /** A human-readable description of the status of this operation. */
  message?: string;
  /** Standard list metadata. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds */
  metadata?: ListMeta;
  /** A machine-readable description of why this operation is in the "Failure" status. If this value is empty there is no information available. A Reason clarifies an HTTP status code but does not override it. */
  reason?: string;
  /** Status of the operation. One of: "Success" or "Failure". More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status */
  status?: string;
};
export type Patch = object;
export type AnnotationActions = {
  canAdd: boolean;
  canDelete: boolean;
  canEdit: boolean;
};
export type AnnotationPermission = {
  dashboard: AnnotationActions;
};
export type DashboardAccess = {
  annotationsPermissions: AnnotationPermission;
  canAdmin: boolean;
  canDelete: boolean;
  canEdit: boolean;
  /** The permissions part */
  canSave: boolean;
  canStar: boolean;
  isPublic: boolean;
  /** Metadata fields */
  slug?: string;
  url?: string;
};
export type DashboardWithAccessInfo = {
  access: DashboardAccess;
  /** APIVersion defines the versioned schema of this representation of an object. Servers should convert recognized schemas to the latest internal value, and may reject unrecognized values. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources */
  apiVersion?: string;
  /** Kind is a string value representing the REST resource this object represents. Servers may infer this from the endpoint the client submits requests to. Cannot be updated. In CamelCase. More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds */
  kind?: string;
  metadata: ObjectMeta;
  /** Spec is the spec of the Dashboard */
  spec: DashboardSpec;
  status: DashboardStatus;
};
export const {
  useGetApiResourcesQuery,
  useLazyGetApiResourcesQuery,
  useListDashboardQuery,
  useLazyListDashboardQuery,
  useCreateDashboardMutation,
  useGetDashboardQuery,
  useLazyGetDashboardQuery,
  useReplaceDashboardMutation,
  useDeleteDashboardMutation,
  useUpdateDashboardMutation,
  useGetDashboardDtoQuery,
  useLazyGetDashboardDtoQuery,
} = injectedRtkApi;
